/**
 * Copyright Mántum All Rights Reserved.
 */
import Validator from '../validator';
import { DataGrid } from './library';

import {
    Suspense,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import type {
    Columns,
    DataGrid as Component,
    DataSource,
    Detail,
    DataTableDetail,
    Result,
    Summaries,
    Validation
} from '@mantum/kanela';

type PropTypes = {
    advanced?: {
        allowAdding?: boolean;
        allowDeleting?: boolean;
        allowExporting?: boolean;
        allowFiltering?: boolean;
        allowPaging?: boolean;
        allowRefresh?: boolean;
        allowReordering?: boolean;
        allowUpdating?: boolean;
        entity?: string;
        height?: number | string;
        mode?: 'infinite' | 'virtual';
        refresh?: number;
        rememberSelection?: boolean;
        actions?: {
            name: string;
            label: string;
            order: number;
        }[];
        revalidate?: boolean;
        selection?: 'none' | 'single' | 'multiple';
        storing?: boolean;
        summary?: {
            result?: Result;
        };
    };
    columns?: Columns;
    dataSource?: DataSource | unknown[];
    detail?: DataTableDetail;
    error?: boolean;
    name?: string;
    onChange?: (name?: string, value?: unknown) => void;
    onClick?: (value: unknown) => void;
    onRowAction?: (name?: string, value?: unknown) => void;
    optional?: boolean;
    readonly?: boolean;
    summaries?: Summaries;
    testid?: string;
    validation?: Validation;
    value?: unknown[];
};

export default forwardRef<Detail<unknown>, PropTypes>(
    function DataTable(props, ref) {
        // prettier-ignore
        const { 
            name, 
            optional = true,
            value = []
        } = props;

        const changeRef = useRef(props.onChange);
        const dataGridRef = useRef<Component>(null);

        const [error, setError] = useState(props.error);

        useEffect(() => {
            changeRef.current = props.onChange;
        }, [props.onChange]);

        useImperativeHandle(
            ref,
            () => {
                return {
                    name,
                    validable: !optional,
                    clear() {
                        setError(false);
                        dataGridRef.current?.clear();
                        if (changeRef.current) {
                            changeRef.current(name, []);
                        }
                    },
                    validate() {
                        setError(false);
                        const result = dataGridRef.current?.validate();
                        if (!result) {
                            setError(true);
                            return false;
                        }
                        return true;
                    },
                    value() {
                        return dataGridRef.current?.value();
                    },
                    refresh() {
                        dataGridRef.current?.refresh();
                    }
                };
            },
            [name, optional]
        );

        const onClick = (value: unknown) => {
            if (props.onClick) {
                props.onClick(value);
            }
        };

        const onRowAction = (name?: string, value?: unknown) => {
            if (props.onRowAction) {
                props.onRowAction(name, value);
            }
        };

        const dataSource = value.length === 0 ? props.dataSource : value;

        return (
            <Validator
                error={error}
                message={props.validation?.message}
                type='datatable'
            >
                <Suspense>
                    <DataGrid
                        actions={props.advanced?.actions}
                        advanced={props.advanced}
                        columns={props.columns}
                        dataSource={dataSource}
                        detail={props.detail}
                        name={props.name}
                        onChange={changeRef.current}
                        onClick={onClick}
                        onRowAction={onRowAction}
                        readonly={props.readonly}
                        ref={dataGridRef}
                        summaries={props.summaries}
                        testid={props.testid}
                    />
                </Suspense>
            </Validator>
        );
    }
);
